'use client';
import useProductContext from '@/context/ProductContext';
import {
  InventoryStatusCode,
  ShopifyProduct,
  getProductInventoryStatus,
} from '@/lib/shopify';
import { interpolate } from '@/utils/translations';

const useInventoryStatus = (
  shopifyProduct: ShopifyProduct | null,
  isOutOfStockDisabled = true,
  isWidthFit = false,
): [string, string, boolean, string, string] => {
  const productContext = useProductContext();

  if (!productContext) {
    return ['', 'btn-responsive', false, '', ''];
  }

  let ctaText = 'Add to Cart';
  let ctaClasses = 'btn-responsive';
  let isCtaDisabled = false;
  let title = '';
  let description = '';

  const { inventoryStatusCopy } = productContext;

  const productTitle = shopifyProduct?.title || 'item';

  // optionally overrides the full width imposed by `btn-responsive` on mobile breakpoints
  if (isWidthFit) {
    ctaClasses += 'w-fit';
  }

  if (shopifyProduct) {
    const inventoryStatus = getProductInventoryStatus(shopifyProduct);

    switch (inventoryStatus) {
      case InventoryStatusCode.OUT_OF_STOCK:
        ctaText = inventoryStatusCopy?.outOfStockCta || 'Out of Stock';
        ctaClasses += isOutOfStockDisabled
          ? ' bg-grey-dark border-grey-dark'
          : ' text-charcoal border-charcoal hover:text-charcoal-dark hover:border-charcoal-dark';
        isCtaDisabled = isOutOfStockDisabled;
        title =
          inventoryStatusCopy?.outOfStockTitle || 'This item is out of stock';
        description =
          interpolate(inventoryStatusCopy?.outOfStockDescription, {
            product: productTitle,
          }) || 'This item is currently out of stock.';
        break;
      case InventoryStatusCode.PRESALE:
        ctaText = inventoryStatusCopy?.preSaleCta || 'Pre-Order';
        break;
      case InventoryStatusCode.ACTIVE:
        ctaText = inventoryStatusCopy?.availableCta || 'Add to Cart';
        break;
      case InventoryStatusCode.BACKORDER:
        ctaText = inventoryStatusCopy?.availableCta || 'Backorder?';
        break;
      default:
        ctaText = inventoryStatusCopy?.availableCta || 'Add to Cart';
        break;
    }
  }

  return [ctaText, ctaClasses, isCtaDisabled, title, description];
};

export default useInventoryStatus;
